import {
    Box,
    Button,
    Collapse,
    Divider,
    Group,
    Popover,
    Stack,
    Text,
    UnstyledButton,
} from "@mantine/core";
import {useDisclosure, useTimeout} from "@mantine/hooks";
import {
    MouseEvent,
    ReactNode,
    RefObject,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import {useTranslation} from "react-i18next";
import IconChevronDown from "../../../assets/chevron-down.svg?react";
import IconChevronUp from "../../../assets/chevron-up.svg?react";
import IconCircleFilled from "../../../assets/circle-filled.svg?react";
import {
    AgentActivity,
    AgentThought,
    KAIResponseType,
    MessageWithMetadata,
    ThoughtType,
} from "../../../types";
import {GPTStreamParser} from "../components/stream-parser/GPTStreamParser";
import scrollClasses from "../components/scrollContainer.module.css";
import classes from "./AssistantProgressTimeline.module.css";

type Props = {
    message: MessageWithMetadata;
};

const HEADER_PROPS = {
    variant: "transparent",
    size: "md",
    w: "100%",
    px: 0,
    justify: "flex-start",
    color: "dark",
} as const;

/**
 * Milliseconds the pointer rests on the row before the card opens. A pointer
 * that only crosses the row leaves the card closed.
 */
const HOVER_OPEN_DELAY = 350;

/**
 * Milliseconds the reasoning card stays open after the pointer leaves. The
 * pointer needs the delay to cross the gap from the row to the card.
 */
const HOVER_CLOSE_DELAY = 150;

/** Pixels between the row and the card below it. */
const CARD_OFFSET = 8;

const bySequence = (a: {sequenceNumber: number}, b: {sequenceNumber: number}) =>
    a.sequenceNumber - b.sequenceNumber;

const isResearchThought = (thought: AgentThought) =>
    (thought.thoughtType ?? thought.type) === ThoughtType.Progress;

/**
 * A run of citation markers at the head of a sentence, such as `[0][2] ` or
 * `[1], [3] `.
 */
const LEADING_CITATIONS = /^\s*((?:\[\d+\]\s*,?\s*)+)/;

/**
 * Moves a run of citation markers back to the sentence they belong to. KAI
 * writes the markers after the full stop of a sentence, and the backend cuts
 * the reasoning stream at the full stop.
 */
const reattachCitations = (sentences: AgentThought[]): string[] => {
    const paragraphs: string[] = [];

    for (const sentence of sentences) {
        const markers = sentence.content.match(LEADING_CITATIONS);
        if (!markers || paragraphs.length === 0) {
            paragraphs.push(sentence.content);
            continue;
        }

        paragraphs[paragraphs.length - 1] += ` ${markers[1].trim()}`;
        const rest = sentence.content.slice(markers[0].length);
        if (rest.trim() !== "") {
            paragraphs.push(rest);
        }
    }

    return paragraphs;
};

/**
 * Groups the reasoning sentences by research operation, then reads each
 * sentence as one paragraph. Parallel operations report the same agentName, so
 * `parentActivityId` separates them. Older threads carry no
 * `parentActivityId`, and their sentences fall back to the agentName group.
 */
export const reasoningParagraphs = (thoughts: AgentThought[]): string[] => {
    const research = thoughts.filter(isResearchThought);
    const material = research.length > 0 ? research : thoughts;

    const byOperation = new Map<string, AgentThought[]>();
    for (const thought of material) {
        const operation = thought.parentActivityId || thought.agentName;
        const sentences = byOperation.get(operation);
        if (sentences) {
            sentences.push(thought);
        } else {
            byOperation.set(operation, [thought]);
        }
    }

    return [...byOperation.values()].flatMap((sentences) =>
        reattachCitations(sentences.sort(bySequence))
    );
};

/** The activity that reports the start of the thinking about the answer. */
const SYNTHESIS_ACTIVITY = "synthesis";

/**
 * Where the thinking about the answer begins. The backend can report the
 * activity more than once for one turn, so the trace takes the first report.
 */
const synthesisSequence = (activities: AgentActivity[]): number | undefined => {
    const reports = activities.filter(
        (activity) => activity.activityType === SYNTHESIS_ACTIVITY
    );

    return reports.length > 0
        ? Math.min(...reports.map((report) => report.sequenceNumber))
        : undefined;
};

/**
 * One row of the trace below the question. Narrations and the thinking share
 * one sequence, so both kinds sort into the order that KAI reports.
 */
type TraceStep =
    | {kind: "narration"; key: string; sequenceNumber: number; text: string}
    | {kind: "thinking"; key: string; sequenceNumber: number};

type TraceRowProps = {
    /** The step KAI works on right now. It carries the shimmer. */
    active: boolean;
    /** Styles the text as the control that opens the reasoning card. */
    hoverable?: boolean;
    /**
     * Builds the row from span elements. A button may not contain a division or
     * a paragraph.
     */
    inline?: boolean;
    /**
     * Holds the row still when it mounts. Only an appended row gets the
     * entrance animation.
     */
    still?: boolean;
    children: ReactNode;
};

const TraceRow = ({
    active,
    hoverable,
    inline,
    still,
    children,
}: TraceRowProps) => (
    <Group
        component={inline ? "span" : "div"}
        className={still ? undefined : classes.row}
        gap="xs"
        wrap="nowrap"
        align="flex-start"
        aria-current={active ? "step" : undefined}
    >
        <span
            className={
                active ? `${classes.icon} ${classes.iconActive}` : classes.icon
            }
            aria-hidden="true"
        >
            <IconCircleFilled />
        </span>
        <Text
            span={inline}
            size="md"
            c="gneis.9"
            className={[
                classes.line,
                hoverable ? classes.hoverable : "",
                active ? classes.lineActive : "",
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {children}
        </Text>
    </Group>
);

/** The width of the reasoning card. The card fills the thread column. */
const cardWidth = (thread: HTMLElement | null): number | undefined =>
    thread?.getBoundingClientRect().width;

type ThinkingRowProps = {
    active: boolean;
    message: MessageWithMetadata;
    reasoning: string[];
    /** The thread column. It bounds the width of the reasoning card. */
    threadRef: RefObject<HTMLDivElement | null>;
};

/**
 * The row that holds the reasoning behind the answer. The card renders nothing
 * until it opens, which keeps a markdown parse out of a card that nobody
 * opens.
 */
const ThinkingRow = ({
    active,
    message,
    reasoning,
    threadRef,
}: ThinkingRowProps) => {
    const {t} = useTranslation();
    const rowRef = useRef<HTMLButtonElement>(null);
    const [hovered, setHovered] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [fromKeyboard, setFromKeyboard] = useState(false);
    const [width, setWidth] = useState<number>();
    const closeAfterPointerOut = useTimeout(
        () => setHovered(false),
        HOVER_CLOSE_DELAY
    );
    const opened = hovered || pinned;

    const show = () => {
        // The thread changes width with the window, so the card measures the
        // column at every open.
        setWidth((current) => cardWidth(threadRef.current) ?? current);
        setHovered(true);
    };

    const openAfterHover = useTimeout(show, HOVER_OPEN_DELAY);

    /** The pointer arrives on the row, or crosses over to the card. */
    const pointerIn = () => {
        closeAfterPointerOut.clear();
        if (opened) {
            openAfterHover.clear();
            return;
        }
        openAfterHover.start();
    };

    const pointerOut = () => {
        openAfterHover.clear();
        closeAfterPointerOut.start();
    };

    const close = () => {
        openAfterHover.clear();
        closeAfterPointerOut.clear();
        setHovered(false);
        setPinned(false);
        setFromKeyboard(false);
    };

    // A pinned card closes on the second click. The pointer rests on the row
    // and sends no further enter event, so the card stays closed.
    const togglePinned = (event: MouseEvent<HTMLButtonElement>) => {
        if (pinned) {
            close();
            return;
        }
        // A click opens the card now, so the hover timer stops.
        openAfterHover.clear();
        // A click from the keyboard reports no pointer, so `detail` is 0. The
        // card then takes the focus, and the escape key closes it.
        setFromKeyboard(event.detail === 0);
        show();
        setPinned(true);
    };

    return (
        <Popover
            opened={opened}
            onChange={(next) => {
                if (!next) {
                    close();
                }
            }}
            position="bottom-start"
            offset={CARD_OFFSET}
            width={width}
            radius={0}
            // The card belongs below the row. Mantine flips it above the row
            // when the space below is short.
            middlewares={{flip: false, shift: true}}
            trapFocus={fromKeyboard}
            returnFocus={fromKeyboard}
        >
            <Popover.Target>
                <UnstyledButton
                    ref={rowRef}
                    className={classes.thinking}
                    onMouseEnter={pointerIn}
                    onMouseLeave={pointerOut}
                    onClick={togglePinned}
                >
                    <TraceRow active={active} hoverable inline>
                        {t("assistant.progress.thinking", {
                            defaultValue: "Thinking about the answer",
                        })}
                    </TraceRow>
                </UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown
                className={classes.reasoningCard}
                p={0}
                onMouseEnter={pointerIn}
                onMouseLeave={pointerOut}
            >
                <div className={classes.reasoningTitle}>
                    <Text size="sm" fw={700} c="dark.5">
                        {t("assistant.progress.reasoningTitle", {
                            defaultValue: "KAI's analysis and assessment",
                        })}
                    </Text>
                </div>
                {/* The scroll box sits inside the card, so the title bar stays
                    in place. A card opened from the keyboard gives the box the
                    focus, so the arrow keys scroll the reasoning. */}
                <div
                    className={`${scrollClasses.scrollbox} ${classes.reasoningScroll}`}
                    tabIndex={-1}
                    data-autofocus
                >
                    <GPTStreamParser
                        text={reasoning.join("\n\n")}
                        meta={message}
                        className={classes.reasoning}
                    />
                </div>
            </Popover.Dropdown>
        </Popover>
    );
};

/**
 * Progress above an assistant answer. The trace grows one row for each step
 * that KAI reports, and the answer heading collapses it once the answer is in.
 */
export const AssistantProgressTimeline = ({message}: Props) => {
    const {t} = useTranslation();
    const [expanded, {toggle}] = useDisclosure(false);
    const panelId = useId();
    const threadRef = useRef<HTMLDivElement>(null);

    const isFinished = message.content.length > 0;

    const reasoning = useMemo(
        () => reasoningParagraphs(message.thoughts ?? []),
        [message.thoughts]
    );

    const synthesis = useMemo(
        () => synthesisSequence(message.activities ?? []),
        [message.activities]
    );

    const hasReasoning = reasoning.length > 0;

    // Every step takes the place its sequence number gives it, so the trace
    // grows at the bottom and nowhere else.
    const steps = useMemo(() => {
        const rows: TraceStep[] = (message.narrations ?? []).map(
            (narration) => ({
                kind: "narration",
                key: narration.narrationId,
                sequenceNumber: narration.sequenceNumber,
                text: narration.text,
            })
        );

        if (hasReasoning && synthesis !== undefined) {
            rows.push({
                kind: "thinking",
                key: "thinking",
                sequenceNumber: synthesis,
            });
        }

        return rows.sort(bySequence);
    }, [message.narrations, hasReasoning, synthesis]);

    // An answer with no progress to show gets no placeholder. The deprecated
    // Fast response type streams no progress, so it gets none either.
    if (
        steps.length === 0 &&
        !hasReasoning &&
        (isFinished || message.responseType === KAIResponseType.Fast)
    ) {
        return null;
    }

    // A message with no synthesis report gives the reasoning row no place of
    // its own. The row then closes the finished trace.
    const trailingThinking =
        hasReasoning && synthesis === undefined && isFinished;

    // The newest row is the step under way while the answer streams. The
    // question carries the shimmer until the first step arrives.
    const activeStep = isFinished ? -1 : steps.length - 1;
    const questionActive = !isFinished && steps.length === 0;

    const trace = (
        <Stack gap={"2xs"}>
            <TraceRow active={questionActive} still>
                {t("reflections.button.text.loading")}
            </TraceRow>
            {steps.map((step, index) =>
                step.kind === "thinking" ? (
                    <ThinkingRow
                        key={step.key}
                        active={index === activeStep}
                        message={message}
                        reasoning={reasoning}
                        threadRef={threadRef}
                    />
                ) : (
                    <TraceRow key={step.key} active={index === activeStep}>
                        {step.text}
                    </TraceRow>
                )
            )}
            {trailingThinking && (
                <ThinkingRow
                    active={false}
                    message={message}
                    reasoning={reasoning}
                    threadRef={threadRef}
                />
            )}
        </Stack>
    );

    return (
        <Box
            ref={threadRef}
            mb={"md"}
            className={
                isFinished
                    ? `${classes.root} ${classes.withHeader}`
                    : classes.root
            }
        >
            {isFinished ? (
                <>
                    <Group w={"100%"} justify="flex-start">
                        <Button
                            {...HEADER_PROPS}
                            onClick={toggle}
                            aria-expanded={expanded}
                            aria-controls={panelId}
                        >
                            <Group gap={3} wrap="nowrap">
                                <Text>{t("assistant.answerHeading")}</Text>
                                {expanded ? (
                                    <IconChevronUp
                                        className={classes.chevron}
                                    />
                                ) : (
                                    <IconChevronDown
                                        className={classes.chevron}
                                    />
                                )}
                            </Group>
                        </Button>
                    </Group>
                    <Collapse id={panelId} in={expanded}>
                        {trace}
                        <Divider my={"lg"} />
                    </Collapse>
                </>
            ) : (
                trace
            )}
        </Box>
    );
};
